// https://base-ui.com/react/components/accordion
import { Accordion as BaseAccordion } from "@base-ui/react/accordion";
import type { ReactNode } from "react";
import { forwardRef, useState } from "react";
import cx from "ui/cx";
import IconChevronDown from "ui/icons/IconMiniChevronDown";
import styles from "./Accordion.module.scss";

interface AccordionPropsBase {
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean; // Sets border left/right to 0;
}

export interface AccordionSingleProps extends AccordionPropsBase {
  type: "single";
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  collapsible?: boolean;
}

export interface AccordionMultipleProps extends AccordionPropsBase {
  type: "multiple";
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
}

export type AccordionProps = AccordionSingleProps | AccordionMultipleProps;

export interface AccordionItemProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  children: ReactNode;
  className?: string;
  header?: ReactNode;
  disabled?: boolean;
}

export interface AccordionHeaderProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children: ReactNode;
  className?: string;
}

export interface AccordionTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  className?: string;
}

export interface AccordionContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  className?: string; // Gets applied to the contentText div
}

// Base UI's Accordion uses a single array `value` plus a `multiple` flag, while our public API keeps
// Radix's `type: "single" | "multiple"` union (single uses a string value). Translate between them.
function toArrayValue(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) {
    return value;
  }
  return value ? [value] : [];
}

function AccordionRoot(props: AccordionProps) {
  const { children, disabled = false, className, fullWidth = false, type } = props;
  const propValue = props.value;
  const onValueChange = props.onValueChange;
  // Radix `single` defaults to non-collapsible (one item always open); Base UI single always allows
  // closing, so guard the empty transition when `collapsible` is not enabled.
  const collapsible = props.type === "single" ? props.collapsible ?? false : true;

  const [internalValue, setInternalValue] = useState<string[]>(() => toArrayValue(propValue ?? props.defaultValue));
  const isControlled = propValue !== undefined;
  const currentValue = isControlled ? toArrayValue(propValue) : internalValue;

  const handleValueChange = (groupValue: string[]) => {
    if (disabled) {
      return;
    }
    if (type === "single") {
      const nextValue = groupValue[groupValue.length - 1] ?? "";
      if (nextValue === "" && !collapsible) {
        return;
      }
      if (!isControlled) {
        setInternalValue(nextValue ? [nextValue] : []);
      }
      (onValueChange as ((value: string) => void) | undefined)?.(nextValue);
      return;
    }
    if (!isControlled) {
      setInternalValue(groupValue);
    }
    (onValueChange as ((value: string[]) => void) | undefined)?.(groupValue);
  };

  return (
    <BaseAccordion.Root
      value={currentValue}
      multiple={type === "multiple"}
      disabled={disabled}
      orientation="vertical"
      onValueChange={handleValueChange}
      className={cx(styles.root, disabled && styles.disabled, fullWidth && styles.fullWidth, className)}
      data-testid="accordion"
    >
      {children}
    </BaseAccordion.Root>
  );
}

AccordionRoot.displayName = "AccordionRoot";

const AccordionItem = forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ children, disabled = false, className, header, ...props }, forwardedRef) => {
    return (
      <BaseAccordion.Item
        disabled={disabled}
        className={cx(styles.item, className)}
        data-testid="accordion-item"
        ref={forwardedRef}
        {...props}
      >
        {header && (
          <AccordionHeader>
            <AccordionTrigger>{header}</AccordionTrigger>
          </AccordionHeader>
        )}
        {children}
      </BaseAccordion.Item>
    );
  },
);

AccordionItem.displayName = "AccordionItem";

const AccordionHeader = forwardRef<HTMLHeadingElement, AccordionHeaderProps>(
  ({ children, className, ...props }, forwardedRef) => {
    return (
      <BaseAccordion.Header
        className={cx(styles.header, className)}
        data-testid="accordion-header"
        ref={forwardedRef}
        {...props}
      >
        {children}
      </BaseAccordion.Header>
    );
  },
);

AccordionHeader.displayName = "AccordionHeader";

const AccordionTrigger = forwardRef<HTMLButtonElement, AccordionTriggerProps>(
  ({ children, className, ...props }, forwardedRef) => {
    return (
      <BaseAccordion.Trigger
        className={cx(styles.trigger, className)}
        data-testid="accordion-trigger"
        ref={forwardedRef}
        {...props}
      >
        {children}
        <div className={styles.chevron} aria-hidden>
          <IconChevronDown />
        </div>
      </BaseAccordion.Trigger>
    );
  },
);

AccordionTrigger.displayName = "AccordionTrigger";

const AccordionContent = forwardRef<HTMLDivElement, AccordionContentProps>(
  ({ children, className, ...props }, forwardedRef) => {
    return (
      <BaseAccordion.Panel className={styles.content} data-testid="accordion-content" ref={forwardedRef} {...props}>
        <div data-testid="accordion-content-text" className={cx(styles.contentText, className)}>
          {children}
        </div>
      </BaseAccordion.Panel>
    );
  },
);

AccordionContent.displayName = "AccordionContent";

// Compound component exports
export const Accordion = {
  Root: AccordionRoot,
  Item: AccordionItem,
  Header: AccordionHeader,
  Trigger: AccordionTrigger,
  Content: AccordionContent,
};

// Individual component exports for flexibility
export { AccordionRoot, AccordionItem, AccordionHeader, AccordionTrigger, AccordionContent };

export default Accordion;
